"use client";

import { useLiveQueryWithStatus } from "@/core/hooks/useLiveQuery";
import { offlineDb, deleteMesureCascade, type OfflineMesure } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

/**
 * Hook pour la gestion des mesures d'un pin dans une campagne donnée (CRUD offline).
 * Modèle B : une mesure est unique par (pin, campagne).
 *
 * @param pinId      Pin cible
 * @param campagneId Campagne active dans laquelle la mesure est prise.
 *                   Si absent, le hook retourne les mesures du pin toutes campagnes
 *                   (mode lecture historique).
 */
export function useMesures(pinId: string, campagneId?: string) {
  const { data: mesures, isLoading } =
    useLiveQueryWithStatus(
      () => {
        const query = offlineDb.mesures.where("pinId").equals(pinId);
        return campagneId
          ? query.filter((m) => m.campagneId === campagneId).toArray()
          : query.toArray();
      },
      [pinId, campagneId]
    );

  const loadedMesures = mesures ?? [];

  /** La mesure principale du pin (la première de la campagne filtrée) */
  const mesure = loadedMesures.length > 0 ? loadedMesures[0] : null;

  /** Crée une mesure vide pour le pin dans la campagne courante (transaction atomique) */
  async function createMesure(): Promise<string> {
    if (!campagneId) {
      throw new Error("campagneId requis pour créer une mesure");
    }

    const id = crypto.randomUUID();
    const now = new Date();

    await offlineDb.transaction("rw", offlineDb.pins, offlineDb.mesures, async () => {
      const pin = await offlineDb.pins.get(pinId);
      const numero = pin?.number ?? 1;

      await offlineDb.mesures.add({
        id,
        numero,
        photos: [],
        notes: "",
        reseau: "",
        localisation: "",
        reseauType: null,
        reseauComplement: null,
        reseauSens: null,
        diametreExt: undefined,
        dn: undefined,
        orientation: "H",
        // Défaut "Acier noir" : ~75% des points de mesure (Renaud peut changer)
        materiau: "Acier noir",
        tarification: [],
        mesureMax: undefined,
        mesureMin: undefined,
        epaisseurOrigine: undefined,
        pourcentageCorrosion: undefined,
        codeCouleur: undefined,
        pinId,
        campagneId,
        createdAt: now,
        updatedAt: now,
        syncStatus: "pending",
      });
    });

    return id;
  }

  /** Met à jour un champ de la mesure (auto-save) */
  async function updateField(
    mesureId: string,
    field: keyof OfflineMesure,
    value: OfflineMesure[keyof OfflineMesure]
  ) {
    const changes = {
      [field]: value,
      updatedAt: new Date(),
      syncStatus: "pending",
    } as Partial<OfflineMesure>;
    await offlineDb.mesures.update(mesureId, changes);
  }

  /** Met à jour plusieurs champs en une seule opération */
  async function updateFields(
    mesureId: string,
    fields: Partial<OfflineMesure>
  ) {
    await offlineDb.mesures.update(mesureId, {
      ...fields,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  /** Supprime une mesure côté serveur puis localement (cascade).
   *  Offline : supprime localement, la sync propagera au serveur. */
  async function deleteMesure(mesureId: string) {
    await deleteWithOfflineFallback("mesure", mesureId, `/api/mesures/${mesureId}`, () =>
      deleteMesureCascade(mesureId)
    );
  }

  return { mesure, mesures: loadedMesures, isLoading, createMesure, updateField, updateFields, deleteMesure };
}
